/**
 * What the four "Other ways to add" do (Option C; plan §3.2, C1 + C3):
 * - Paste connection string (C3): on the start view it reads the clipboard into the filter (an explicit click), in the
 *   form view it opens the paste field above the form (`onPaste`); nothing is applied before Continue / Apply.
 * - Detect local databases opens the Docker / native discovery dialog ON TOP of this one (drafts kept).
 * - Import from another tool opens the source menu; a source's review (or the Azure picker) opens ON TOP of this dialog
 *   (C3: it used to leave the dialog); cancelling returns to the same view, engine and field, and a completed import
 *   tells the dialog through `connectionDialogHostStore` (`useNestedFlows`).
 * - Browse Azure (C3.1) opens the picker ON TOP of this dialog, unfiltered and multi-select: one pick opens that
 *   engine's filled form (`useDialogNavigation.applyAzurePick`), several open the Import review over the dialog
 *   (`openAzureReview`, completing through the host like an import); Cancel returns to the same view, engine and field.
 *
 * Every use fires `feature:connection_entry_point { entry, view }`. While a Save is in flight all four are unavailable
 * with the reason "Saving…" (C0); Detect is also unavailable, with its own reason, where Docker is the only source and
 * absent.
 */
import { useRef, useState } from 'react';
import type { TrackFunction } from '@/core/analytics/analyticsTypes';
import { useDetectLocalDatabases } from '../../docker-discovery';
import type { EntryPlacement, EntryPointId } from '../start/entryPoints';
import { FUNNEL_VERSION } from './useConnectionDialogFunnel';

type Options = {
  track: TrackFunction;
  /** Set while a Save is in flight ("Saving…"). */
  savingReason?: string;
  onPaste: (placement: EntryPlacement) => void;
  /** Browse Azure: open the entry-mode picker on top of the dialog (C3.1). */
  onBrowseAzure: (placement: EntryPlacement) => void;
};

export function useEntryPoints({ track, savingReason, onPaste, onBrowseAzure }: Options) {
  const detect = useDetectLocalDatabases();
  const [importMenuAt, setImportMenuAt] = useState<EntryPlacement | null>(null);
  // The entry a nested flow was opened from, so its dialog can hand focus back to it (C3).
  const lastEntry = useRef<EntryPointId>('import');

  const reasonFor = (id: EntryPointId): string | undefined => savingReason ?? (id === 'detect' ? detect.unavailableReason : undefined);

  const activate = (id: EntryPointId, placement: EntryPlacement) => {
    if (reasonFor(id)) return;
    track('feature:connection_entry_point', { entry: id, view: placement, funnel_version: FUNNEL_VERSION });
    lastEntry.current = id;
    if (id === 'paste') onPaste(placement);
    else if (id === 'detect') detect.open(placement);
    else if (id === 'import') setImportMenuAt(placement);
    else onBrowseAzure(placement);
  };

  return {
    reasonFor,
    activate,
    /** The import source menu is open at this placement (a start option, a rail row or the compact bar). */
    importMenuAt,
    closeImportMenu: () => setImportMenuAt(null),
    /** The entry point used last (the one a nested flow returns focus to). */
    lastEntry: () => lastEntry.current,
  };
}

export type EntryPointsApi = ReturnType<typeof useEntryPoints>;
